import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { InfoPageHeader } from '@/components/brand/InfoPageHeader';

const AllergensInfo = () => {
  // Initialize language from localStorage or default to Latvian
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language') || localStorage.getItem('selectedLanguage');
    return savedLanguage || 'lv';
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    en: {
      title: "Allergen Information",
      subtitle: "The 14 allergens: These are the key allergens that restaurants must declare",
      allergens: [
        { number: 1, name: "Celery" },
        { number: 2, name: "Cereals containing gluten (e.g., wheat, barley, oats)" },
        { number: 3, name: "Crustaceans (e.g., crab, lobster, prawn)" },
        { number: 4, name: "Eggs" },
        { number: 5, name: "Fish" },
        { number: 6, name: "Lupin" },
        { number: 7, name: "Milk" },
        { number: 8, name: "Molluscs (e.g., mussels, oysters)" },
        { number: 9, name: "Mustard" },
        { number: 10, name: "Peanuts" },
        { number: 11, name: "Sesame" },
        { number: 12, name: "Soybeans" },
        { number: 13, name: "Sulphur dioxide and sulphites (at concentrations of more than 10mg/kg or 10mg/L)" },
        { number: 14, name: "Nuts, which refers to tree nuts" }
      ],
      backToHome: "Back to Home"
    },
    ru: {
      title: "Информация об аллергенах",
      subtitle: "14 аллергенов: Это ключевые аллергены, которые должны декларировать рестораны",
      allergens: [
        { number: 1, name: "Сельдерей" },
        { number: 2, name: "Злаки, содержащие глютен (например, пшеница, ячмень, овёс)" },
        { number: 3, name: "Ракообразные (например, краб, лобстер, креветки)" },
        { number: 4, name: "Яйца" },
        { number: 5, name: "Рыба" },
        { number: 6, name: "Люпин" },
        { number: 7, name: "Молоко" },
        { number: 8, name: "Моллюски (например, мидии, устрицы)" },
        { number: 9, name: "Горчица" },
        { number: 10, name: "Арахис" },
        { number: 11, name: "Кунжут" },
        { number: 12, name: "Соя" },
        { number: 13, name: "Диоксид серы и сульфиты (при концентрации более 10 мг/кг или 10 мг/л)" },
        { number: 14, name: "Орехи (древесные орехи)" }
      ],
      backToHome: "Вернуться на главную"
    },
    lv: {
      title: "Informācija par alergēniem",
      subtitle: "14 alergēni: Tie ir galvenie alergēni, kurus restorāniem ir jādeklarē",
      allergens: [
        { number: 1, name: "Selerija" },
        { number: 2, name: "Glutēnu saturoši labības produkti (piemēram, kvieši, mieži, auzas)" },
        { number: 3, name: "Vēžveidīgie (piemēram, krabis, omārs, garnele)" },
        { number: 4, name: "Olas" },
        { number: 5, name: "Zivis" },
        { number: 6, name: "Lupīna" },
        { number: 7, name: "Piens" },
        { number: 8, name: "Mīkstmieši (piemēram, gliemenes, austeres)" },
        { number: 9, name: "Sinepes" },
        { number: 10, name: "Zemesrieksti" },
        { number: 11, name: "Sezama sēklas" },
        { number: 12, name: "Sojas pupas" },
        { number: 13, name: "Sēra dioksīds un sulfīti (koncentrācijā vairāk nekā 10 mg/kg vai 10 mg/l)" },
        { number: 14, name: "Rieksti (koku rieksti)" }
      ],
      backToHome: "Atpakaļ uz sākumlapu"
    }
  };

  const t = content[language];

  return (
    <div className="rz-page min-h-screen" data-testid="allergens-page">
      <InfoPageHeader language={language} setLanguage={setLanguage} title={t.title} subtitle={t.subtitle} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-[#E7E5E4]">
          <div className="p-8">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <div className="space-y-4">
                  {t.allergens.map((allergen) => (
                    <div 
                      key={allergen.number}
                      className="flex items-start border-b border-[#E7E5E4] pb-3 last:border-0"
                    >
                      <div className="flex-shrink-0 w-10">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F5F2EC] text-[#1C1917] font-bold text-sm">
                          {allergen.number}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[#1C1917]">{allergen.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a 
            href="/" 
            className="inline-flex items-center text-[#57534E] hover:text-[#1C1917] transition-colors"
          >
            ← {t.backToHome}
          </a>
        </div>
      </div>
    </div>
  );
};

export default AllergensInfo;
